/**
 * Link is implemented using ReactRouter.Link APIs.
 *
 * Please refer to https://v5.reactrouter.com/web/api/Link/to-object for detailed documentation.
 */
import React, { memo } from 'react';
import { useIntl } from 'react-intl';
import { Link as ReactRouterLink } from 'react-router-dom-v5-compat';
import { Layout, useStyles } from 'uinix-ui';

import { SharedLinkProps } from '../../types/SharedLinkProps';
import { Icon } from '../Icon/Icon';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

export const Link = memo(
  ({
    children,
    color,
    disabled,
    display = 'flex',
    hash,
    href,
    isActive,
    isInverse,
    isNav,
    maxW,
    pathname = '/',
    search,
    shouldTruncate = false,
    state,
    text,
    theme = 'light',
    tooltip,
    variant,
    onClick,
    enableNewTab = Boolean(href),
  }: SharedLinkProps) => {
    const intl = useIntl();
    const styles = useStyles();
    const isDarkTheme = theme === 'dark';
    const isInline = display === 'inline-flex';

    const handleClick = (
      event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
      if (disabled) {
        event.preventDefault();
        return;
      } else if (onClick) {
        // prevents default navigation event explicitly and run the onClick handler if provided
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }
    };

    const TextComponent = shouldTruncate ? TruncateText : Text;

    const textContent = (
      <Tooltip tooltip={tooltip}>
        <TextComponent
          color={color || (isInverse ? 'inverse.text.link' : 'action.link')}
          disabled={disabled}
          styles={styles.link}
          maxW={maxW}
          styleProps={{ isActive, isNav, isDarkTheme }}
          variant={variant}
        >
          {text ?? children}
        </TextComponent>
      </Tooltip>
    );

    const contents = enableNewTab ? (
      <Layout align="center" display={display}>
        {textContent}
        <Icon
          commentMore
          actions
          disabled={disabled}
          icon="launch"
          tooltip={intl.formatMessage({
            id: 'Link.launchTooltip',
            defaultMessage: 'Opens in a new tab',
            description:
              'Tooltip for launch icon indicating link opens in a new tab',
          })}
        />
      </Layout>
    ) : isInline ? (
      <Layout display="inline-flex">{textContent}</Layout>
    ) : (
      textContent
    );

    const commonProps = {
      'aria-current': isActive ? ('page' as const) : undefined,
      disabled,
      target: enableNewTab ? '_blank' : undefined,
      onClick: handleClick,
      style: { overflow: shouldTruncate ? 'hidden' : '' },
    };

    if (href) {
      return (
        <a {...commonProps} href={href}>
          {contents}
        </a>
      );
    } else {
      return (
        <ReactRouterLink
          {...commonProps}
          state={state}
          to={{
            hash,
            pathname,
            search,
          }}
        >
          {contents}
        </ReactRouterLink>
      );
    }
  },
);
